import { useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import Dialog from 'Components/Molecules/Dialog/Dialog';
import IconWarning from 'Components/Atoms/IconWarning/IconWarning';
import Button from 'Components/Atoms/Button/Button';
import Radio from 'Components/Atoms/Radio/Radio';
import RadioGroup from 'Components/Atoms/Radio/RadioGroup';
import Switch from 'Components/Atoms/Switch/Switch';
import { formatCurrency } from 'Utils/formatters';
import Divider from 'Components/Atoms/Divider/Divider';

const MISMATCH_OPTIONS = {
    RECONCILE_WITH_DIFFERENCE: 'reconcile-with-difference',
    ADD_TRANSACTION_LATER: 'add-transaction-later',
};

export default function ReconciliationAmountMismatchDialog(props) {
    const { open, onClose, onReconcile, onSave, itemType = 'transaction', mismatchAmount, currency = 'EUR' } = props;

    const { t } = useTranslation('reconciliation');
    const [selectedOption, setSelectedOption] = useState(MISMATCH_OPTIONS.RECONCILE_WITH_DIFFERENCE);
    const [forceAssociatedReconciliationStatus, setForceAssociatedReconciliationStatus] = useState(false);

    const handleSubmit = () => {
        if (selectedOption === MISMATCH_OPTIONS.RECONCILE_WITH_DIFFERENCE) {
            const key =
                itemType === 'transaction'
                    ? 'forceTransactionsReconciliationStatus'
                    : 'forceInvoicesReconciliationStatus';

            onReconcile({
                [key]: forceAssociatedReconciliationStatus ? true : null,
            });
        } else {
            onSave();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className="max-w-xl">
            <div className="text-center py-4">
                <div className="flex justify-center mb-4">
                    <IconWarning size={48} color="#6366f1" />
                </div>

                <div className="mx-auto max-w-lg">
                    <h2 className="text-xl font-bold text-dark-blue mb-2">{t('amount-mismatch.title')}</h2>
                    <div className="text-gray-500 text-sm text-pretty">
                        <Trans
                            t={t}
                            i18nKey={
                                itemType === 'transaction'
                                    ? 'amount-mismatch.description-invoice'
                                    : 'amount-mismatch.description-transaction'
                            }
                            values={{ amount: formatCurrency(Math.abs(mismatchAmount), { currency }) }}
                            components={{ bold: <strong className="font-semibold text-dark-blue" />, br: <br /> }}
                        />
                    </div>
                    <div className="mx-auto max-w-md text-left py-8 mb-3">
                        <RadioGroup
                            name="mismatch-option"
                            direction="column"
                            className="gap-5"
                            value={selectedOption}
                            onChange={(e) => setSelectedOption(e.target.value)}
                        >
                            <Radio value={MISMATCH_OPTIONS.RECONCILE_WITH_DIFFERENCE} className="items-start gap-3">
                                <div className="text-dark-blue font-semibold mb-0.5">
                                    {t('amount-mismatch.option-reconcile-with-difference')}
                                </div>
                                <div className="text-gray-500 text-sm">
                                    {t(
                                        itemType === 'transaction'
                                            ? 'amount-mismatch.option-reconcile-with-difference-description-invoice'
                                            : 'amount-mismatch.option-reconcile-with-difference-description-transaction',
                                    )}
                                </div>
                            </Radio>
                            <Radio value={MISMATCH_OPTIONS.ADD_TRANSACTION_LATER} className="items-start gap-3">
                                <div className="text-dark-blue font-semibold mb-0.5">
                                    {t(
                                        itemType === 'transaction'
                                            ? 'amount-mismatch.option-add-invoice-later'
                                            : 'amount-mismatch.option-add-transaction-later',
                                    )}
                                </div>
                                <div className="text-gray-500 text-sm">
                                    {t(
                                        itemType === 'transaction'
                                            ? 'amount-mismatch.option-add-invoice-later-description'
                                            : 'amount-mismatch.option-add-transaction-later-description',
                                    )}
                                </div>
                            </Radio>
                        </RadioGroup>
                        {selectedOption === MISMATCH_OPTIONS.RECONCILE_WITH_DIFFERENCE && (
                            <>
                                <Divider className="my-5" />

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <Switch
                                        checked={forceAssociatedReconciliationStatus}
                                        onCheckedChange={setForceAssociatedReconciliationStatus}
                                    />
                                    <span className="text-sm font-medium text-dark-blue leading-snug">
                                        {t(
                                            itemType === 'transaction'
                                                ? 'amount-mismatch.option-mark-associated-as-reconciled-invoice'
                                                : 'amount-mismatch.option-mark-associated-as-reconciled-transaction',
                                        )}
                                    </span>
                                </label>
                            </>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Button variant="primary" onClick={handleSubmit}>
                            {t('confirm-reconciliation')}
                        </Button>
                        <Button onClick={onClose}>{t('cancel')}</Button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

ReconciliationAmountMismatchDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onReconcile: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    itemType: PropTypes.oneOf(['transaction', 'invoice']),
    mismatchAmount: PropTypes.number.isRequired,
    currency: PropTypes.string,
};
